import { useState, Fragment } from 'react'
import { intersection } from 'lodash-es'
import { useApolloClient } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import Popup from '@/ui/Popup'
import buildQueryVariables from '@/helpers/build-query-variables'
import { Icon, Loader } from '@/ui'
import useTableStore from '@/stores/useTableStore'
import { CheckAnchor } from './Styles'

interface CheckButtonProps {
  tableName: string
  total: number
  data: any[]
  checkboxKey: string
  onCheckAll: (ids: string[], uncheckAll?: boolean) => void
  graphileFilters?: any
  popupPosition?:
    | 'bottom left'
    | 'top left'
    | 'top right'
    | 'bottom right'
    | 'right center'
    | 'left center'
    | 'top center'
    | 'bottom center'
  onCheckAllQuery?: any
  queryVariables?: any
}

const CheckButton = ({
  tableName,
  total,
  data,
  checkboxKey,
  onCheckAll,
  graphileFilters,
  popupPosition = 'bottom left',
  ...props
}: CheckButtonProps) => {
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)
  const [hasCheckedBoxes, setHasCheckedBoxes] = useState(false)
  const [checkAllLoading, setCheckAllLoading] = useState(false)

  const client = useApolloClient()

  const table = useTableStore(state =>
    state.tables.find(x => x.tableName === tableName),
  )

  const currentChecked = table?.checked ?? []

  const handleOnCheckAll = async (
    onlyCheckVisible = false,
    uncheckAll = false,
  ) => {
    try {
      if (onlyCheckVisible || uncheckAll) {
        onCheckAll([], uncheckAll)
      } else {
        setCheckAllLoading(true)

        const variables = graphileFilters
          ? graphileFilters
          : buildQueryVariables({
              ...props.queryVariables,
            })

        const query =
          typeof props.onCheckAllQuery === 'function'
            ? props.onCheckAllQuery(variables)
            : props.onCheckAllQuery

        if (!onlyCheckVisible) {
          const { data } = await client.query({
            query,
            variables,
          })

          const key = Object.keys(data)[0]

          onCheckAll(
            data[key].edges.map(({ node }: any) => node.id),
            uncheckAll,
          )
        }
      }
    } catch (e) {
    } finally {
      setOpen(false)
      setCheckAllLoading(false)
    }
  }

  const handleOnOpen = async () => {
    const hasCheckedBoxes =
      intersection(
        table?.checked,
        data.map(x => x[checkboxKey]),
      ).length !== data.length

    setHasCheckedBoxes(hasCheckedBoxes)

    setOpen(true)
  }

  return (
    <Popup
      open={open}
      on="click"
      position={popupPosition}
      popperModifiers={[{ name: 'preventOverflow' }]}
      onOpen={handleOnOpen}
      onClose={() => setOpen(false)}
      content={
        <Fragment>
          <CheckAnchor onClick={async () => handleOnCheckAll(true)}>
            {hasCheckedBoxes
              ? t('Check all displayed')
              : t('Uncheck all displayed')}
          </CheckAnchor>

          {props.onCheckAllQuery && (
            <CheckAnchor
              disabled={checkAllLoading}
              onClick={() => handleOnCheckAll()}>
              {t('Check all')}
              {checkAllLoading && (
                <Loader active inline size="tiny" color="gray" />
              )}
            </CheckAnchor>
          )}

          {currentChecked.length > 0 && (
            <CheckAnchor
              disabled={checkAllLoading}
              onClick={() => handleOnCheckAll(false, true)}>
              {t('Uncheck all')}
            </CheckAnchor>
          )}
        </Fragment>
      }
      trigger={<Icon button name="list layout" size="small" />}
    />
  )
}

export default CheckButton
